import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { colors, fontWeight, TypographyStyledGlobal } from '../../theme';
import useFetcher from '../../utils/hooks/useFetcher';
import { DashboardParams, ScopeProps, ScopeLabelProps, TemporalScopeFromApi } from '../Dashboard/dashboardInterfaces';
import getDefaultDateRange from '../Dashboard/TemporalScope/defaultDateRange';
import TemporalScope from '../Dashboard/TemporalScope/temporalScope';
import Loader from '../Loader/loader';
import { SurveyProps } from '../Survey/surveyInterface';
import { SurveyDefaultDatePeriod, SurveyTemporalFilterType, SurveyTemporalityType } from '../Surveys/surveyInterface';
import { GetHotAlertsInterface, KPI_TYPE, HotAlertsCountInterface, HistoricalTemporalityOption } from './hotAlertsInterface';
import { HotAlertHeader } from './styled';
import SummaryBox from './SummaryBox/SummaryBox';
import VirtualizedHotAlertTable from './Table/virtualizedHotAlertTable';

const HotAlertsSummary: FC = () => {
    const { t } = useTranslation(['hotAlerts', 'navigation']);
    const [hotAlerts, setHotAlerts] = useState<GetHotAlertsInterface>({ total: 0, columns: [], satisfactionConfiguration: { questionId: 0, type: KPI_TYPE.SATISFACTION, positiveModalityIds: [], negativeModalityIds: [] }, status: [], datas: [] });
    const [numberOfFilteredHotAlerts, setnumberOfFilteredHotAlerts] = useState<number>(0);
    const { surveyId = '' } = useParams<DashboardParams>();
    const [hotAlertCounts, setHotAlertCounts] = useState<HotAlertsCountInterface>({ numberToTreat: 0, numberOfClosed: 0 });
    const fetcher = useFetcher();
    const { data } = useSWR(`${process.env.REACT_APP_API_URL}/survey`, useFetcher());
    const { data: fetchedSurvey } = useSWR(`${process.env.REACT_APP_API_URL}/survey/${surveyId}`, fetcher);
    const surveyName = data?.find((survey: SurveyProps) => String(survey.surveyid) === surveyId)?.name;
    const [selectedScope, setSelectedScope] = useState<Array<ScopeProps>>([]);
    const [dateRange, setDateRange] = useState<any>(() => getDefaultDateRange(SurveyDefaultDatePeriod.ALL));
    const hasDateRange = Boolean(dateRange.selection.startDate && dateRange.selection.endDate);
    const [selectedScopeLabel, setSelectedScopeLabel] = useState<Array<ScopeLabelProps>>([]);
    // const [temporalScopes, setTemporalScope] = useState<TemporalScopeFromApi[]>([]);
    const selectedScopes = fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.VAGUE && JSON.stringify(selectedScope.map((scope: ScopeProps) => ({ [scope.scopeId]: scope.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })));
    const selectedDateRanges = fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE && hasDateRange && JSON.stringify({ startDate: dateRange?.selection.startDate.toLocaleDateString('fr-CA'), endDate: dateRange?.selection.endDate.toLocaleDateString('fr-CA'), key: dateRange?.selection.key });
    const filterScopeArray = [selectedScope.length > 0 ? `scopes=${selectedScopes}` : '', selectedDateRanges ? `dateRanges=${selectedDateRanges}` : ''].filter((fs: string) => fs !== '');
    const filterScopeLink = filterScopeArray.length > 0 ? `/?${filterScopeArray.join('&')}` : '';
    const { data: fetchedHotAlerts, error: hotAlertsError } = useSWR([`${process.env.REACT_APP_API_URL}/survey/${surveyId}/hot_alert${filterScopeLink}`], fetcher);
    const isLoadingHotAlerts = !fetchedHotAlerts && !hotAlertsError;
    // Holds a wave question id for VAGUE surveys, or a SurveyTemporalityType for DATE surveys.
    const [historicalTemporality, setHistoricalTemporality] = useState<string>('');
    const historicalDisplayLink = (() => {
        if (!historicalTemporality) {
            return '';
        }
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.VAGUE) {
            return `?question_id=${historicalTemporality}`;
        }
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE) {
            return `?temporality="${historicalTemporality}"`;
        }

        return '';
    })();
    const historicalTemporalityOptions: HistoricalTemporalityOption[] = (() => {
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.VAGUE && fetchedSurvey.temporalScope) {
            return fetchedSurvey.temporalScope.map((temporal: TemporalScopeFromApi) => ({
                value: Object.keys(temporal.scope)[0],
                label: Object.values(temporal.scope)[0],
            }));
        }
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE) {
            return [
                { value: SurveyTemporalityType.WEEK, label: t('hotAlerts:summary.temporalities.week') },
                { value: SurveyTemporalityType.MONTH, label: t('hotAlerts:summary.temporalities.month') },
                { value: SurveyTemporalityType.QUARTER, label: t('hotAlerts:summary.temporalities.quarter') },
                { value: SurveyTemporalityType.YEAR, label: t('hotAlerts:summary.temporalities.year') },
            ];
        }

        return [];
    })();
    const { data: historical } = useSWR(historicalDisplayLink && [`${process.env.REACT_APP_API_URL}/survey/${surveyId}/hot_alert/historical/${historicalDisplayLink}`], fetcher);
    const handleChangeDateRangeScope = (newDateRange: any): void => {
        setDateRange(newDateRange);
    };

    useEffect(() => {
        if (fetchedHotAlerts) {
            setHotAlerts(fetchedHotAlerts);
        }
    }, [fetchedHotAlerts]);

    const handleSelectedScope = ([scopeId, modalityId]: [string, Array<string> | null]): void => {
        const indexOf = selectedScope.findIndex((alreadySelected: ScopeProps) => alreadySelected.scopeId === scopeId);

        if (modalityId !== null && modalityId?.length !== 0) {
            if (indexOf === -1) {
                setSelectedScope([...selectedScope, { scopeId, modalityId }]);
            } else {
                const newSelectedScope = [...selectedScope];
                newSelectedScope[indexOf] = { scopeId, modalityId };
                setSelectedScope(newSelectedScope);
            }
        } else {
            const newSelectedScope = [...selectedScope];
            newSelectedScope.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedScope(newSelectedScope);
            }
        }
    };

    const handleSelectedScopeLabel = ([scopeId, scopeLabel, modalitiesLabel]: [number, string, string | null]): void => {
        const indexOf = selectedScopeLabel.findIndex((alreadySelectedLabel: ScopeLabelProps) => alreadySelectedLabel.scopeId === scopeId);

        if (modalitiesLabel !== null) {
            if (indexOf === -1) {
                setSelectedScopeLabel([...selectedScopeLabel, { scopeId, scopeLabel, modalitiesLabel }]);
            } else {
                const newSelectedScopeLabel = [...selectedScopeLabel];
                newSelectedScopeLabel[indexOf] = { scopeId, scopeLabel, modalitiesLabel };
                setSelectedScopeLabel(newSelectedScopeLabel);
            }
        } else {
            const newSelectedScopeLabel = [...selectedScopeLabel];
            newSelectedScopeLabel.splice(indexOf, 1);
            if (indexOf !== -1) {
                setSelectedScopeLabel(newSelectedScopeLabel);
            }
        }
    };

    useEffect(() => {
        if (!fetchedSurvey) {
            return;
        }
        if (fetchedSurvey.temporalFilterType === SurveyTemporalFilterType.VAGUE && fetchedSurvey.temporalScope?.length > 0) {
            setHistoricalTemporality(Object.keys(fetchedSurvey.temporalScope[0].scope)[0]);
        }
        if (fetchedSurvey.temporalFilterType === SurveyTemporalFilterType.DATE) {
            setHistoricalTemporality(SurveyTemporalityType.MONTH);
        }
    }, [fetchedSurvey?.surveyid, fetchedSurvey?.temporalFilterType]);

    useEffect(() => {
        if (fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE) {
            setDateRange(getDefaultDateRange(fetchedSurvey.defaultDatePeriod));
        }
    }, [fetchedSurvey?.surveyid, fetchedSurvey?.temporalFilterType, fetchedSurvey?.defaultDatePeriod]);

    return (
        <>
            <HotAlertHeader>
                <TypographyStyledGlobal
                    color={colors.grey200}
                    margin='0 auto 0 0'
                    size='24px'
                    weight={fontWeight.semiBold}
                >
                    { surveyName }
                </TypographyStyledGlobal>
                <TemporalScope
                    dateRange={dateRange}
                    handleChangeDateRangeScope={handleChangeDateRangeScope}
                    handleSelectedScope={handleSelectedScope}
                    handleSelectedScopeLabel={handleSelectedScopeLabel}
                    maxDate={new Date()}
                    minDate={new Date(fetchedSurvey?.minDate)}
                    selectedScope={selectedScope}
                    selectedScopeLabel={selectedScopeLabel}
                    temporalFilterType={fetchedSurvey?.temporalFilterType}
                    temporalScopes={fetchedSurvey?.temporalScope ? fetchedSurvey?.temporalScope : []}
                    isForHotAlert
                />
            </HotAlertHeader>
            <SummaryBox
                displayExport={fetchedSurvey?.canExportHotAlert}
                handleChangeHistoricalTemporality={setHistoricalTemporality}
                hasStatus={hotAlerts.status?.length > 0}
                historical={historical}
                historicalTemporalityOptions={historicalTemporalityOptions}
                hotAlertCounts={hotAlertCounts}
                numberOfhotAlerts={hotAlerts.total}
                selectedDateRange={fetchedSurvey?.temporalFilterType === SurveyTemporalFilterType.DATE && hasDateRange ? dateRange.selection : null}
                selectedHistoricalTemporality={historicalTemporality}
                selectedScope={selectedScope}
                surveyId={surveyId}
            />
            {!isLoadingHotAlerts && (
                <>
                    <div>
                        <TypographyStyledGlobal
                            color={colors.grey200}
                            margin='0 0 24px 0'
                            size='21px'
                            weight={fontWeight.semiBold}
                        >

                            { numberOfFilteredHotAlerts === hotAlerts.total ? t('hotAlerts:summary.count', { count: numberOfFilteredHotAlerts }) : t('hotAlerts:summary.countFiltered', { count: numberOfFilteredHotAlerts })}

                        </TypographyStyledGlobal>
                    </div>
                    <VirtualizedHotAlertTable
                        handleHotAlertCounts={setHotAlertCounts}
                        handleNumberOfFilteredHotAlerts={setnumberOfFilteredHotAlerts}
                        hotalerts={hotAlerts}
                        surveyId={surveyId}
                        temporalFilterType={fetchedSurvey?.temporalFilterType}
                    />
                </>
            )}
            {isLoadingHotAlerts && (
                <Loader />
            )}
        </>
    );
};

export default HotAlertsSummary;
